import * as React from 'react'
import { useIntl } from 'react-intl'
import { capitalize } from 'lodash'
import { CircularProgress, TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import RoundedAddIcon from '../../../../assets/icons/RoundedAdd.svg'
import { StudentGroupState, StudentState } from '../../../../features/types'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    studentsData: StudentGroupState[]
    studentsInfo: StudentState[]
    isFetchingStudents: boolean
    isAddingStudent: boolean
    isLoadingStudentsInfo: boolean
    addStudentToGroup: (courseUserEnrollId: number, courseSubjectId: number) => void
}

const NoGroupStudentsTable: React.FC<Props> = ({
    studentsData = [],
    studentsInfo = [],
    isFetchingStudents = false,
    isAddingStudent = false,
    isLoadingStudentsInfo = false,
    addStudentToGroup,
}) => {
    const { formatMessage, locale } = useIntl()

    const getFullName = (userId: number) => {
        const student = studentsInfo.find((student) => student.id === userId)

        if (student) {
            return student.firstName + ' ' + student.lastName
        }
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'course' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'language' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'actions' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row) => (
                                <TableRow key={row.userId}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isLoadingStudentsInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getFullName(row.userId)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.groupTitle}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {
                                            row[
                                                `courseTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell>
                                        {
                                            row[
                                                `subjectsTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell>{row.language}</TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="center"
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{ background: ui.track }}
                                            disabled={isAddingStudent}
                                            onClick={() =>
                                                addStudentToGroup(
                                                    row?.userCourseEnrollId, row?.courseSubjectId
                                                )
                                            }
                                        >
                                            <img
                                                src={RoundedAddIcon}
                                                alt="add"
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}

                        </TableBody>
                    ) : (
                        <TableBody>
                            <TableRow>
                                {!isFetchingStudents && (
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={7}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableBody>
                    )}

                </Table>
            </TableContainer>
        </Box>
    )
}

export default NoGroupStudentsTable
